// Chemin : C:\gestion-copro\src\pages\vigie\ContactsVigiePage.tsx
// Session 122 : Page Mes contacts — CRUD interlocuteurs simplifié pour VIGIE
// Session 151 : Ajout couleurs pour types notaire, conseil_syndical, collectivite, coproprietaire
// Session 160 : Dropdown hybride fonction par type, regroupement par catégorie
// Session 174 : Ajout couleur type locataire, types personne physique
// Session 283 : Refonte cosmétique — avatars initiales colorés, boutons action directs
// Session 293 : Multi-contacts par fiche — liste orderable, premier = contact par défaut
//               Affichage liste : tous les contacts visibles sous la raison sociale

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Plus, Edit2, Trash2, X, Check, Phone, Mail, MapPin, Search, AlertCircle, ChevronUp, ChevronDown, UserPlus } from 'lucide-react';
import { useCopropriete } from '../../context/CoproprieteContext';

import { useAideContextuelle } from '../../hooks/useAideContextuelle';
import AideContextuelleModule from '../../components/ui/AideContextuelleModule';
import { AIDE_CONTACTS } from '../../constants/aideContextuelle';
import {
  getInterlocuteurs,
  createInterlocuteur,
  updateInterlocuteur,
  deleteInterlocuteur,
  TYPES_INTERLOCUTEUR,
  getTypeInterlocuteurLabel
} from '../../services/interlocuteur.service';
import { Interlocuteur, TypeInterlocuteur, Contact, Civilite, FONCTIONS_PAR_TYPE } from '../../types/interlocuteur.types';

// ============================================
// TYPES LOCAUX
// ============================================

interface ContactFormEntry {
  civilite: Civilite;
  nom: string;
  prenom: string;
  fonction: string;
  fonctionAutre: string;
  email: string;
  telephoneFixe: string;
  telephoneMobile: string;
}

const CONTACT_ENTRY_INITIAL: ContactFormEntry = {
  civilite: '',
  nom: '',
  prenom: '',
  fonction: '',
  fonctionAutre: '',
  email: '',
  telephoneFixe: '',
  telephoneMobile: '',
};

interface FicheFormData {
  id?: string;
  type: TypeInterlocuteur;
  raisonSociale: string;
  adresseRue: string;
  adresseCodePostal: string;
  adresseVille: string;
  notes: string;
  contactEntries: ContactFormEntry[];
}

const FORM_INITIAL: FicheFormData = {
  type: 'syndic',
  raisonSociale: '',
  adresseRue: '',
  adresseCodePostal: '',
  adresseVille: '',
  notes: '',
  contactEntries: [{ ...CONTACT_ENTRY_INITIAL }],
};

// ============================================
// COULEURS PAR TYPE
// ============================================

const TYPE_COLORS: Record<TypeInterlocuteur, { bg: string; text: string; border: string; avatarBg: string; avatarText: string }> = {
  syndic:           { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    avatarBg: 'bg-blue-100',    avatarText: 'text-blue-700' },
  promoteur:        { bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200',  avatarBg: 'bg-orange-100',  avatarText: 'text-orange-700' },
  constructeur:     { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   avatarBg: 'bg-amber-100',   avatarText: 'text-amber-700' },
  prestataire:      { bg: 'bg-green-50',   text: 'text-green-700',   border: 'border-green-200',   avatarBg: 'bg-green-100',   avatarText: 'text-green-700' },
  assurance:        { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  avatarBg: 'bg-purple-100',  avatarText: 'text-purple-700' },
  avocat:           { bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200',     avatarBg: 'bg-red-100',     avatarText: 'text-red-700' },
  notaire:          { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200',    avatarBg: 'bg-rose-100',    avatarText: 'text-rose-700' },
  expert:           { bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200',  avatarBg: 'bg-indigo-100',  avatarText: 'text-indigo-700' },
  conseil_syndical: { bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200',    avatarBg: 'bg-teal-100',    avatarText: 'text-teal-700' },
  collectivite:     { bg: 'bg-cyan-50',    text: 'text-cyan-700',    border: 'border-cyan-200',    avatarBg: 'bg-cyan-100',    avatarText: 'text-cyan-700' },
  coproprietaire:   { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', avatarBg: 'bg-emerald-100', avatarText: 'text-emerald-700' },
  locataire:        { bg: 'bg-lime-50',    text: 'text-lime-700',    border: 'border-lime-200',    avatarBg: 'bg-lime-100',    avatarText: 'text-lime-700' },
  autre:            { bg: 'bg-gray-50',    text: 'text-gray-700',    border: 'border-gray-200',    avatarBg: 'bg-gray-100',    avatarText: 'text-gray-700' },
};

const FONCTION_AUTRE = '__autre__';
const ORDRE_TYPES: TypeInterlocuteur[] = TYPES_INTERLOCUTEUR.map(t => t.value);
const TYPES_PERSONNE_PHYSIQUE: TypeInterlocuteur[] = ['locataire', 'coproprietaire'];

const getInitiales = (raisonSociale: string): string => {
  const mots = raisonSociale.trim().split(/\s+/).filter(Boolean);
  if (mots.length === 0) return '?';
  if (mots.length === 1) return mots[0].substring(0, 2).toUpperCase();
  return (mots[0][0] + mots[1][0]).toUpperCase();
};

// Convertit un ContactFormEntry en Contact Firestore
const entryToContact = (entry: ContactFormEntry, index: number, type: TypeInterlocuteur): Record<string, any> => {
  const fonctionsDisponibles = FONCTIONS_PAR_TYPE[type] || [];
  let fonctionFinale = '';
  if (fonctionsDisponibles.length === 0) {
    fonctionFinale = entry.fonctionAutre.trim() || entry.fonction.trim();
  } else if (entry.fonction === FONCTION_AUTRE) {
    fonctionFinale = entry.fonctionAutre.trim();
  } else {
    fonctionFinale = entry.fonction;
  }

  const contact: Record<string, any> = {
    nom: entry.nom.trim(),
    principal: index === 0,
  };
  if (entry.prenom.trim()) contact.prenom = entry.prenom.trim();
  if (entry.civilite) contact.civilite = entry.civilite;
  if (fonctionFinale) contact.fonction = fonctionFinale;
  if (entry.email.trim()) contact.email = entry.email.trim();
  if (entry.telephoneFixe.trim()) contact.telephone = entry.telephoneFixe.trim();
  if (entry.telephoneMobile.trim()) contact.telephoneMobile = entry.telephoneMobile.trim();
  return contact;
};

// Convertit un Contact Firestore en ContactFormEntry pour l'édition
const contactToEntry = (contact: Contact, type: TypeInterlocuteur): ContactFormEntry => {
  const fonctionsDisponibles = FONCTIONS_PAR_TYPE[type] || [];
  const fonctionExistante = contact.fonction || '';
  const estPredéfinie = fonctionsDisponibles.includes(fonctionExistante);
  const estSaisieLibre = fonctionExistante && !estPredéfinie && fonctionsDisponibles.length > 0;

  return {
    civilite: (contact as any).civilite || '',
    nom: contact.nom || '',
    prenom: contact.prenom || '',
    fonction: estSaisieLibre ? FONCTION_AUTRE : fonctionExistante,
    fonctionAutre: estSaisieLibre ? fonctionExistante : '',
    email: contact.email || '',
    telephoneFixe: contact.telephone || '',
    telephoneMobile: (contact as any).telephoneMobile || '',
  };
};

// ============================================
// SOUS-COMPOSANT : Formulaire d'un contact individuel
// ============================================

interface ContactEntryFormProps {
  entry: ContactFormEntry;
  index: number;
  total: number;
  type: TypeInterlocuteur;
  isPersonnePhysique: boolean;
  onUpdate: (index: number, updated: ContactFormEntry) => void;
  onRemove: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
}

const ContactEntryForm: React.FC<ContactEntryFormProps> = ({
  entry, index, total, type, isPersonnePhysique,
  onUpdate, onRemove, onMoveUp, onMoveDown
}) => {
  const fonctionsDisponibles = FONCTIONS_PAR_TYPE[type] || [];
  const showFonctionDropdown = fonctionsDisponibles.length > 0;
  const showFonctionLibre = !showFonctionDropdown || entry.fonction === FONCTION_AUTRE;

  const update = (field: keyof ContactFormEntry, value: string) => {
    onUpdate(index, { ...entry, [field]: value });
  };

  const handleFonctionSelect = (value: string) => {
    if (value === FONCTION_AUTRE) {
      onUpdate(index, { ...entry, fonction: FONCTION_AUTRE, fonctionAutre: entry.fonctionAutre || '' });
    } else {
      onUpdate(index, { ...entry, fonction: value, fonctionAutre: '' });
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
      {/* En-tête contact */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {index === 0 ? 'Contact par défaut' : `Contact ${index + 1}`}
          </span>
          {index === 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
              Par défaut
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {index > 0 && (
            <button
              type="button"
              onClick={() => onMoveUp(index)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
              title="Remonter (devient contact par défaut)"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          )}
          {index < total - 1 && (
            <button
              type="button"
              onClick={() => onMoveDown(index)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
              title="Descendre"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
          {total > 1 && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors ml-1"
              title="Supprimer ce contact"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Ligne : Civilité + Nom + Prénom */}
      <div className="grid grid-cols-5 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Civilité</label>
          <select
            value={entry.civilite}
            onChange={(e) => update('civilite', e.target.value)}
            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
          >
            <option value="">—</option>
            <option value="M.">M.</option>
            <option value="Mme">Mme</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {isPersonnePhysique ? 'Nom *' : 'Nom'}
          </label>
          <input
            type="text"
            value={entry.nom}
            onChange={(e) => update('nom', e.target.value)}
            placeholder={isPersonnePhysique ? 'Ex: LENFLE' : 'Ex: Dupont'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Prénom</label>
          <input
            type="text"
            value={entry.prenom}
            onChange={(e) => update('prenom', e.target.value)}
            placeholder="Ex: Jean"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
          />
        </div>
      </div>

      {/* Ligne : Fonction + Email + Téléphone */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Fonction</label>
          {showFonctionDropdown ? (
            <div className="space-y-2">
              <select
                value={entry.fonction === FONCTION_AUTRE ? FONCTION_AUTRE : entry.fonction}
                onChange={(e) => handleFonctionSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
              >
                <option value="">— Sélectionner —</option>
                {fonctionsDisponibles.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
                <option value={FONCTION_AUTRE}>Autre (saisie libre)</option>
              </select>
              {showFonctionLibre && (
                <input
                  type="text"
                  value={entry.fonctionAutre}
                  onChange={(e) => update('fonctionAutre', e.target.value)}
                  placeholder="Préciser..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                />
              )}
            </div>
          ) : (
            <input
              type="text"
              value={entry.fonctionAutre || entry.fonction}
              onChange={(e) => update('fonctionAutre', e.target.value)}
              placeholder="Ex: Responsable, Gérant..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
            />
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
          <input
            type="email"
            value={entry.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="contact@exemple.fr"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone</label>
          <input
            type="tel"
            value={entry.telephoneFixe}
            onChange={(e) => update('telephoneFixe', e.target.value)}
            placeholder="03 28 76 91 10"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
          />
        </div>
      </div>

      {/* Alerte email manquant sur contact par défaut */}
      {!entry.email && index === 0 && (
        <p className="text-xs text-orange-500 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Email non renseigné — SENTINELLE ne pourra pas préparer d{"'"}email
        </p>
      )}
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const ContactsVigiePage: React.FC = () => {
  const { coproprieteId } = useCopropriete();
  const aide = useAideContextuelle({ moduleKey: 'contacts' });

  const [contacts, setContacts] = useState<Interlocuteur[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<FicheFormData>(FORM_INITIAL);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ============================================
  // CHARGEMENT
  // ============================================

  const loadContacts = useCallback(async () => {
    if (!coproprieteId) return;
    try {
      setLoading(true);
      const data = await getInterlocuteurs(coproprieteId, true);
      setContacts(data);
    } catch (err) {
      console.error('Erreur chargement contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [coproprieteId]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // ============================================
  // FILTRAGE + REGROUPEMENT
  // ============================================

  const filteredContacts = useMemo(() => {
    return contacts.filter(c => {
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase();
      return (
        c.raisonSociale.toLowerCase().includes(term) ||
        getTypeInterlocuteurLabel(c.type).toLowerCase().includes(term) ||
        c.contacts?.some(ct =>
          ct.email?.toLowerCase().includes(term) ||
          ct.nom?.toLowerCase().includes(term) ||
          ct.fonction?.toLowerCase().includes(term)
        )
      );
    });
  }, [contacts, searchTerm]);

  const contactsParCategorie = useMemo(() => {
    const groupes: { type: TypeInterlocuteur; label: string; contacts: Interlocuteur[] }[] = [];
    for (const type of ORDRE_TYPES) {
      const contactsDuType = filteredContacts.filter(c => c.type === type);
      if (contactsDuType.length > 0) {
        groupes.push({ type, label: getTypeInterlocuteurLabel(type), contacts: contactsDuType });
      }
    }
    return groupes;
  }, [filteredContacts]);

  // ============================================
  // GESTION TYPE
  // ============================================

  const handleTypeChange = (newType: TypeInterlocuteur) => {
    const newEntries = formData.contactEntries.map(entry => ({
      ...entry,
      fonction: '',
      fonctionAutre: '',
    }));
    setFormData({ ...formData, type: newType, contactEntries: newEntries });
  };

  // ============================================
  // GESTION CONTACTS (tableau)
  // ============================================

  const updateContactEntry = (index: number, updated: ContactFormEntry) => {
    const newEntries = [...formData.contactEntries];
    newEntries[index] = updated;
    setFormData({ ...formData, contactEntries: newEntries });
  };

  const addContactEntry = () => {
    setFormData({
      ...formData,
      contactEntries: [...formData.contactEntries, { ...CONTACT_ENTRY_INITIAL }],
    });
  };

  const removeContactEntry = (index: number) => {
    const newEntries = formData.contactEntries.filter((_, i) => i !== index);
    setFormData({ ...formData, contactEntries: newEntries });
  };

  const moveContactUp = (index: number) => {
    if (index === 0) return;
    const newEntries = [...formData.contactEntries];
    [newEntries[index - 1], newEntries[index]] = [newEntries[index], newEntries[index - 1]];
    setFormData({ ...formData, contactEntries: newEntries });
  };

  const moveContactDown = (index: number) => {
    if (index >= formData.contactEntries.length - 1) return;
    const newEntries = [...formData.contactEntries];
    [newEntries[index], newEntries[index + 1]] = [newEntries[index + 1], newEntries[index]];
    setFormData({ ...formData, contactEntries: newEntries });
  };

  // ============================================
  // OUVRIR FORMULAIRE
  // ============================================

  const openNewForm = () => {
    setFormData(FORM_INITIAL);
    setIsEditing(false);
    setFormError('');
    setShowForm(true);
  };

  const openEditForm = (interlocuteur: Interlocuteur) => {
    // Trier : principal d'abord, puis les autres
    const contactsTries = [...(interlocuteur.contacts || [])].sort((a, b) => {
      if (a.principal && !b.principal) return -1;
      if (!a.principal && b.principal) return 1;
      return 0;
    });

    const entries = contactsTries.length > 0
      ? contactsTries.map(c => contactToEntry(c, interlocuteur.type))
      : [{ ...CONTACT_ENTRY_INITIAL }];

    setFormData({
      id: interlocuteur.id,
      type: interlocuteur.type,
      raisonSociale: interlocuteur.raisonSociale,
      adresseRue: interlocuteur.adresse?.rue || '',
      adresseCodePostal: interlocuteur.adresse?.codePostal || '',
      adresseVille: interlocuteur.adresse?.ville || '',
      notes: interlocuteur.notes || '',
      contactEntries: entries,
    });
    setIsEditing(true);
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData(FORM_INITIAL);
    setIsEditing(false);
    setFormError('');
  };

  // ============================================
  // ENREGISTRER
  // ============================================

  const handleSave = async () => {
    if (!coproprieteId) return;

    const isPhysique = TYPES_PERSONNE_PHYSIQUE.includes(formData.type);
    const premierContact = formData.contactEntries[0];

    if (!premierContact?.nom?.trim()) {
      setFormError(isPhysique ? 'Le nom est obligatoire' : 'Le nom du premier contact est obligatoire');
      return;
    }

    const raisonSocialeFinale = isPhysique
      ? [premierContact.nom.trim(), premierContact.prenom.trim()].filter(Boolean).join(' ')
      : formData.raisonSociale.trim();

    if (!raisonSocialeFinale) {
      setFormError('Le nom / raison sociale est obligatoire');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const contactsFirestore = formData.contactEntries
        .filter(e => e.nom.trim() || e.email.trim())
        .map((entry, idx) => entryToContact(entry, idx, formData.type));

      if (contactsFirestore.length === 0) {
        setFormError('Au moins un contact avec un nom est requis');
        setSaving(false);
        return;
      }

      const adresse = (formData.adresseRue.trim() || formData.adresseCodePostal.trim() || formData.adresseVille.trim())
        ? { rue: formData.adresseRue.trim(), codePostal: formData.adresseCodePostal.trim(), ville: formData.adresseVille.trim() }
        : undefined;

      const cleanData: Record<string, any> = {
        type: formData.type,
        raisonSociale: raisonSocialeFinale,
        contacts: contactsFirestore,
      };
      if (adresse) cleanData.adresse = adresse;
      if (formData.notes.trim()) cleanData.notes = formData.notes.trim();

      if (isEditing && formData.id) {
        await updateInterlocuteur(formData.id, cleanData);
      } else {
        await createInterlocuteur({ coproprieteId, ...cleanData, actif: true } as any);
      }

      closeForm();
      await loadContacts();
    } catch (err) {
      console.error('Erreur sauvegarde contact:', err);
      setFormError("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // RÉORDONNER CONTACTS DIRECTEMENT DANS LA LISTE
  // ============================================

  const [reorderingId, setReorderingId] = useState<string | null>(null);

  const moveContactInList = async (interlocuteur: Interlocuteur, fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= interlocuteur.contacts.length) return;
    setReorderingId(interlocuteur.id);
    try {
      const newContacts = [...interlocuteur.contacts];
      [newContacts[fromIdx], newContacts[toIdx]] = [newContacts[toIdx], newContacts[fromIdx]];
      // Recalculer principal : toujours le premier
      const contactsFirestore = newContacts.map((c, idx) => ({ ...c, principal: idx === 0 }));
      await updateInterlocuteur(interlocuteur.id, { contacts: contactsFirestore } as any);
      await loadContacts();
    } catch (err) {
      console.error('Erreur réordonnancement:', err);
    } finally {
      setReorderingId(null);
    }
  };

  // ============================================
  // SUPPRIMER
  // ============================================

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      await deleteInterlocuteur(id);
      setDeleteConfirmId(null);
      await loadContacts();
    } catch (err) {
      console.error('Erreur suppression contact:', err);
    } finally {
      setDeleting(false);
    }
  };

  // ============================================
  // HELPERS AFFICHAGE
  // ============================================

  const isPersonnePhysique = TYPES_PERSONNE_PHYSIQUE.includes(formData.type);

  const formatNomContact = (c: Contact): string => {
    const parts = [c.prenom, c.nom].filter(Boolean);
    return parts.join(' ');
  };

  // ============================================
  // RENDU
  // ============================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <Users className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Mes contacts</h1>
            <p className="text-sm text-gray-500">
              {contacts.length} fiche{contacts.length !== 1 ? 's' : ''} enregistrée{contacts.length !== 1 ? 's' : ''}
              {contactsParCategorie.length > 0 && ` · ${contactsParCategorie.length} catégorie${contactsParCategorie.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        <button
          onClick={openNewForm}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium text-sm"
        >
          <Plus className="w-4 h-4" />
          Ajouter un contact
        </button>
      </div>

      {/* Aide contextuelle */}
      {aide.aideChargee && (
        <AideContextuelleModule
          config={AIDE_CONTACTS}
          aideAccueilVue={aide.aideAccueilVue}
          afficherAideComplete={aide.afficherAideComplete}
          onFermer={aide.fermerAideAccueil}
          onVoirPlus={aide.ouvrirAideComplete}
        />
      )}

      {/* Recherche */}
      {contacts.length > 3 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300"
          />
        </div>
      )}

      {/* ============================================
          FORMULAIRE INLINE
          ============================================ */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-amber-200 mb-6 overflow-hidden">
          <div className="px-5 py-4 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <h2 className="font-semibold text-amber-800">
              {isEditing ? 'Modifier la fiche' : 'Nouvelle fiche contact'}
            </h2>
            <button onClick={closeForm} className="text-amber-600 hover:text-amber-800">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 space-y-5">

            {/* Identité de la fiche — entité pro */}
            {!isPersonnePhysique && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleTypeChange(e.target.value as TypeInterlocuteur)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                  >
                    {TYPES_INTERLOCUTEUR.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom / Raison sociale *</label>
                  <input
                    type="text"
                    value={formData.raisonSociale}
                    onChange={(e) => setFormData({ ...formData, raisonSociale: e.target.value })}
                    placeholder="Ex: Square Habitat Nord de France"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                  />
                </div>
              </div>
            )}

            {/* Type pour personne physique */}
            {isPersonnePhysique && (
              <div className="w-40">
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => handleTypeChange(e.target.value as TypeInterlocuteur)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                >
                  {TYPES_INTERLOCUTEUR.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* ── Section Contacts ── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-sm font-semibold text-gray-700">Contacts</span>
                  <span className="ml-2 text-xs text-gray-400">
                    Le premier de la liste est utilisé par défaut par SENTINELLE
                  </span>
                </div>
                <button
                  type="button"
                  onClick={addContactEntry}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Ajouter un contact
                </button>
              </div>

              <div className="space-y-3">
                {formData.contactEntries.map((entry, idx) => (
                  <ContactEntryForm
                    key={idx}
                    entry={entry}
                    index={idx}
                    total={formData.contactEntries.length}
                    type={formData.type}
                    isPersonnePhysique={isPersonnePhysique}
                    onUpdate={updateContactEntry}
                    onRemove={removeContactEntry}
                    onMoveUp={moveContactUp}
                    onMoveDown={moveContactDown}
                  />
                ))}
              </div>
            </div>

            {/* Adresse */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse postale</label>
              <input
                type="text"
                value={formData.adresseRue}
                onChange={(e) => setFormData({ ...formData, adresseRue: e.target.value })}
                placeholder="Rue"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 mb-2"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={formData.adresseCodePostal}
                  onChange={(e) => setFormData({ ...formData, adresseCodePostal: e.target.value })}
                  placeholder="Code postal"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
                <input
                  type="text"
                  value={formData.adresseVille}
                  onChange={(e) => setFormData({ ...formData, adresseVille: e.target.value })}
                  placeholder="Ville"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes personnelles (gestionnaire habituel, horaires, etc.)"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
              />
            </div>

            {/* Erreur */}
            {formError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                {formError}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={closeForm}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                <Check className="w-4 h-4" />
                {saving ? 'Enregistrement...' : (isEditing ? 'Mettre à jour' : 'Enregistrer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          LISTE PAR CATÉGORIE
          ============================================ */}
      {filteredContacts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-10 text-center">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            {searchTerm ? 'Aucun résultat' : 'Aucune fiche enregistrée'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {searchTerm
              ? "Essaie avec d'autres termes de recherche."
              : "Ajoute tes interlocuteurs (syndic, promoteur, assurance...) pour que SENTINELLE puisse préparer tes correspondances."}
          </p>
          {!searchTerm && (
            <button
              onClick={openNewForm}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Ajouter ma première fiche
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {contactsParCategorie.map(groupe => {
            const colors = TYPE_COLORS[groupe.type] || TYPE_COLORS.autre;

            return (
              <div key={groupe.type}>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className={`text-sm font-semibold ${colors.text}`}>{groupe.label}</h2>
                  <span className="text-xs text-gray-400 font-medium">({groupe.contacts.length})</span>
                  <div className="flex-1 border-t border-gray-100" />
                </div>

                <div className="space-y-2 ml-2">
                  {groupe.contacts.map(interlocuteur => {
                    const initiales = getInitiales(interlocuteur.raisonSociale);
                    const tousContacts = [...(interlocuteur.contacts || [])].sort((a, b) => {
                      if (a.principal && !b.principal) return -1;
                      if (!a.principal && b.principal) return 1;
                      return 0;
                    });
                    const contactPrincipal = tousContacts[0];

                    return (
                      <div
                        key={interlocuteur.id}
                        className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-150"
                      >
                        <div className="p-4 flex items-start gap-4">

                          {/* Avatar */}
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${colors.avatarBg}`}>
                            <span className={`text-sm font-bold ${colors.avatarText}`}>{initiales}</span>
                          </div>

                          {/* Infos */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 mb-1.5">
                              {interlocuteur.raisonSociale}
                            </h3>

                            {/* Session 293 : Tous les contacts visibles */}
                            <div className="space-y-1.5">
                              {tousContacts.map((ct, ctIdx) => {
                                const nomAffiche = formatNomContact(ct);
                                const isReordering = reorderingId === interlocuteur.id;
                                const multiContacts = tousContacts.length > 1;
                                return (
                                  <div key={ctIdx} className="flex items-center gap-2">
                                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                      {nomAffiche && (
                                        <span className={`text-sm truncate ${ctIdx === 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                                          {ct.civilite && <span className="text-gray-400">{ct.civilite} </span>}
                                          {nomAffiche}
                                        </span>
                                      )}
                                      {ct.fonction && (
                                        <span className="text-xs text-gray-400 flex-shrink-0">· {ct.fonction}</span>
                                      )}
                                      {/* Bouton "Définir par défaut" sur les contacts non-premiers */}
                                      {multiContacts && ctIdx > 0 && (
                                        <button
                                          onClick={() => moveContactInList(interlocuteur, ctIdx, 0)}
                                          disabled={isReordering}
                                          className="text-xs text-amber-600 hover:text-amber-700 hover:underline flex-shrink-0 disabled:opacity-50"
                                          title="Définir comme contact par défaut"
                                        >
                                          Définir par défaut
                                        </button>
                                      )}
                                    </div>
                                    {/* Boutons email/tel */}
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      {ct.email && (
                                        <a
                                          href={`mailto:${ct.email}`}
                                          className={`p-1.5 rounded-lg transition-colors ${colors.avatarBg} ${colors.avatarText} hover:opacity-80`}
                                          title={ct.email}
                                        >
                                          <Mail className="w-3.5 h-3.5" />
                                        </a>
                                      )}
                                      {(ct.telephone || (ct as any).telephoneMobile) && (
                                        <a
                                          href={`tel:${ct.telephone || (ct as any).telephoneMobile}`}
                                          className={`p-1.5 rounded-lg transition-colors ${colors.avatarBg} ${colors.avatarText} hover:opacity-80`}
                                          title={ct.telephone || (ct as any).telephoneMobile}
                                        >
                                          <Phone className="w-3.5 h-3.5" />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Adresse */}
                            {interlocuteur.adresse?.ville && (
                              <p className="text-xs text-gray-400 flex items-center gap-1 mt-1.5">
                                <MapPin className="w-3 h-3" />
                                {interlocuteur.adresse.codePostal} {interlocuteur.adresse.ville}
                              </p>
                            )}

                            {/* Notes */}
                            {interlocuteur.notes && (
                              <p className="text-xs text-gray-400 mt-0.5 italic truncate">{interlocuteur.notes}</p>
                            )}

                            {/* Alerte email manquant sur contact principal */}
                            {!contactPrincipal?.email && (
                              <p className="text-xs text-orange-500 mt-1 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Email non renseigné — SENTINELLE ne pourra pas préparer d{"'"}email
                              </p>
                            )}
                          </div>

                          {/* Actions fiche */}
                          <div className="flex items-center gap-1 flex-shrink-0 self-start">
                            <div className="w-px h-5 bg-gray-200 mx-1" />
                            <button
                              onClick={() => openEditForm(interlocuteur)}
                              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>

                            {deleteConfirmId === interlocuteur.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleDelete(interlocuteur.id)}
                                  disabled={deleting}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Confirmer"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmId(null)}
                                  className="p-2 text-gray-400 hover:bg-gray-50 rounded-lg transition-colors"
                                  title="Annuler"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirmId(interlocuteur.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContactsVigiePage;
